import * as Schema from "effect/Schema";
import { NonNegativeInt, PositiveInt, TrimmedNonEmptyString } from "./baseSchemas.ts";
import { DesignSystemMentionTarget } from "./designSystemMention.ts";

const PROJECT_SEARCH_ENTRIES_MAX_LIMIT = 200;
const PROJECT_WRITE_FILE_PATH_MAX_LENGTH = 512;
const PROJECT_READ_FILE_PATH_MAX_LENGTH = 512;

export const ProjectCreateAfarInput = Schema.Struct({
  destinationPath: TrimmedNonEmptyString,
});
export type ProjectCreateAfarInput = typeof ProjectCreateAfarInput.Type;

export const ProjectCreateAfarStage = Schema.Literals([
  "destination-check",
  "directory-creation",
  "git-initialization",
  "layout-write",
  "references-write",
  "validation",
  "final-move",
]);
export type ProjectCreateAfarStage = typeof ProjectCreateAfarStage.Type;

export const ProjectCreateAfarFailure = Schema.Literals([
  "destination-exists",
  "destination-not-directory",
  "destination-not-writable",
  "destination-parent-unavailable",
  "destination-parent-not-directory",
  "destination-parent-not-writable",
  "git-unavailable",
  "temporary-directory-create-failed",
  "git-initialization-failed",
  "layout-write-failed",
  "references-write-failed",
  "project-validation-failed",
  "final-move-failed",
]);
export type ProjectCreateAfarFailure = typeof ProjectCreateAfarFailure.Type;

export class ProjectCreateAfarError extends Schema.TaggedErrorClass<ProjectCreateAfarError>()(
  "ProjectCreateAfarError",
  {
    destinationPath: TrimmedNonEmptyString,
    stage: ProjectCreateAfarStage,
    reason: ProjectCreateAfarFailure,
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {}

export const ProjectSearchEntriesInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  query: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  limit: PositiveInt.check(Schema.isLessThanOrEqualTo(PROJECT_SEARCH_ENTRIES_MAX_LIMIT)),
});
export type ProjectSearchEntriesInput = typeof ProjectSearchEntriesInput.Type;

const ProjectEntryKind = Schema.Literals(["file", "directory"]);

export const ProjectEntry = Schema.Struct({
  path: TrimmedNonEmptyString,
  kind: ProjectEntryKind,
});
export type ProjectEntry = typeof ProjectEntry.Type;

export const ProjectSearchEntriesResult = Schema.Struct({
  entries: Schema.Array(ProjectEntry),
  truncated: Schema.Boolean,
});
export type ProjectSearchEntriesResult = typeof ProjectSearchEntriesResult.Type;

export const ProjectDiscoverDesignSystemMentionsInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
});
export type ProjectDiscoverDesignSystemMentionsInput =
  typeof ProjectDiscoverDesignSystemMentionsInput.Type;

export const ProjectDiscoverDesignSystemMentionsResult = Schema.Struct({
  targets: Schema.Array(DesignSystemMentionTarget),
  completeness: Schema.Literals(["complete", "partial"]),
});
export type ProjectDiscoverDesignSystemMentionsResult =
  typeof ProjectDiscoverDesignSystemMentionsResult.Type;

export class ProjectDiscoverDesignSystemMentionsError extends Schema.TaggedErrorClass<ProjectDiscoverDesignSystemMentionsError>()(
  "ProjectDiscoverDesignSystemMentionsError",
  {
    cwd: TrimmedNonEmptyString,
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: { readonly cwd: string; readonly cause?: unknown }) {
    super({
      ...props,
      message: `Could not discover Design Systems in '${props.cwd}'.`,
    } as any);
  }
}

export const ProjectListEntriesInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
});
export type ProjectListEntriesInput = typeof ProjectListEntriesInput.Type;

export const ProjectListEntriesResult = Schema.Struct({
  entries: Schema.Array(ProjectEntry),
  truncated: Schema.Boolean,
});
export type ProjectListEntriesResult = typeof ProjectListEntriesResult.Type;

export const ProjectRefreshEntriesInput = ProjectListEntriesInput;
export type ProjectRefreshEntriesInput = typeof ProjectRefreshEntriesInput.Type;

export const ProjectRefreshEntriesResult = ProjectListEntriesResult;
export type ProjectRefreshEntriesResult = typeof ProjectRefreshEntriesResult.Type;

export const ProjectInspectAfarInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  readOnly: Schema.optionalKey(Schema.Boolean),
});
export type ProjectInspectAfarInput = typeof ProjectInspectAfarInput.Type;

export const ProjectAfarWorkspaceIneligibilityReason = Schema.Literals([
  "workspace-not-present",
  "workspace-path-invalid",
  "workspace-inspection-unavailable",
]);
export type ProjectAfarWorkspaceIneligibilityReason =
  typeof ProjectAfarWorkspaceIneligibilityReason.Type;

function projectAfarWorkspaceReadModel(id: "brain" | "design" | "schedule") {
  return Schema.Union([
    Schema.Struct({
      id: Schema.Literal(id),
      presence: Schema.Literal("present"),
      capabilityEligibility: Schema.Literal("eligible"),
    }),
    Schema.Struct({
      id: Schema.Literal(id),
      presence: Schema.Literal("not-present"),
      capabilityEligibility: Schema.Literal("ineligible"),
      reason: Schema.Literal("workspace-not-present"),
      recovery: TrimmedNonEmptyString,
    }),
    Schema.Struct({
      id: Schema.Literal(id),
      presence: Schema.Literal("invalid"),
      capabilityEligibility: Schema.Literal("ineligible"),
      reason: Schema.Literal("workspace-path-invalid"),
      recovery: TrimmedNonEmptyString,
    }),
    Schema.Struct({
      id: Schema.Literal(id),
      presence: Schema.Literal("unavailable"),
      capabilityEligibility: Schema.Literal("ineligible"),
      reason: Schema.Literal("workspace-inspection-unavailable"),
      recovery: TrimmedNonEmptyString,
    }),
  ]);
}

export const ProjectAfarWorkspaceReadModel = Schema.Union([
  projectAfarWorkspaceReadModel("brain"),
  projectAfarWorkspaceReadModel("design"),
  projectAfarWorkspaceReadModel("schedule"),
]);
export type ProjectAfarWorkspaceReadModel = typeof ProjectAfarWorkspaceReadModel.Type;

export const ProjectAfarWorkspaceReadModels = Schema.Tuple([
  projectAfarWorkspaceReadModel("brain"),
  projectAfarWorkspaceReadModel("design"),
  projectAfarWorkspaceReadModel("schedule"),
]);
export type ProjectAfarWorkspaceReadModels = typeof ProjectAfarWorkspaceReadModels.Type;

export const ProjectAfarOptionalContentRootIneligibilityReason = Schema.Literals([
  "optional-root-path-invalid",
  "optional-root-inspection-unavailable",
  "optional-root-unsupported-by-release",
]);
export type ProjectAfarOptionalContentRootIneligibilityReason =
  typeof ProjectAfarOptionalContentRootIneligibilityReason.Type;

function projectAfarOptionalContentRootReadModel(
  id: "experiment" | "workflows",
  relativePath: ".afar-research/experiment" | ".afar-research/workflows",
) {
  const identity = {
    id: Schema.Literal(id),
    relativePath: Schema.Literal(relativePath),
  } as const;
  return Schema.Union([
    Schema.Struct({
      ...identity,
      presence: Schema.Literals(["present", "not-present"]),
      pathSafety: Schema.Literal("safe"),
      accessibility: Schema.Literal("accessible"),
      releaseSupport: Schema.Literal("supported"),
      capabilityEligibility: Schema.Literal("eligible"),
    }),
    Schema.Struct({
      ...identity,
      presence: Schema.Literal("present"),
      pathSafety: Schema.Literal("unsafe"),
      accessibility: Schema.Literal("accessible"),
      releaseSupport: Schema.Literals(["supported", "unsupported"]),
      capabilityEligibility: Schema.Literal("ineligible"),
      reason: Schema.Literal("optional-root-path-invalid"),
      recovery: TrimmedNonEmptyString,
    }),
    Schema.Struct({
      ...identity,
      presence: Schema.Literal("unknown"),
      pathSafety: Schema.Literal("unsafe"),
      accessibility: Schema.Literal("unknown"),
      releaseSupport: Schema.Literals(["supported", "unsupported"]),
      capabilityEligibility: Schema.Literal("ineligible"),
      reason: Schema.Literal("optional-root-path-invalid"),
      recovery: TrimmedNonEmptyString,
    }),
    Schema.Struct({
      ...identity,
      presence: Schema.Literal("present"),
      pathSafety: Schema.Literal("safe"),
      accessibility: Schema.Literal("unavailable"),
      releaseSupport: Schema.Literals(["supported", "unsupported"]),
      capabilityEligibility: Schema.Literal("ineligible"),
      reason: Schema.Literal("optional-root-inspection-unavailable"),
      recovery: TrimmedNonEmptyString,
    }),
    Schema.Struct({
      ...identity,
      presence: Schema.Literal("unknown"),
      pathSafety: Schema.Literal("unknown"),
      accessibility: Schema.Literal("unavailable"),
      releaseSupport: Schema.Literals(["supported", "unsupported"]),
      capabilityEligibility: Schema.Literal("ineligible"),
      reason: Schema.Literal("optional-root-inspection-unavailable"),
      recovery: TrimmedNonEmptyString,
    }),
    Schema.Struct({
      ...identity,
      presence: Schema.Literals(["present", "not-present"]),
      pathSafety: Schema.Literal("safe"),
      accessibility: Schema.Literal("accessible"),
      releaseSupport: Schema.Literal("unsupported"),
      capabilityEligibility: Schema.Literal("ineligible"),
      reason: Schema.Literal("optional-root-unsupported-by-release"),
      recovery: TrimmedNonEmptyString,
    }),
  ]);
}

export const ProjectAfarOptionalContentRootReadModel = Schema.Union([
  projectAfarOptionalContentRootReadModel("experiment", ".afar-research/experiment"),
  projectAfarOptionalContentRootReadModel("workflows", ".afar-research/workflows"),
]);
export type ProjectAfarOptionalContentRootReadModel =
  typeof ProjectAfarOptionalContentRootReadModel.Type;

export const ProjectAfarOptionalContentRootReadModels = Schema.Tuple([
  projectAfarOptionalContentRootReadModel("experiment", ".afar-research/experiment"),
  projectAfarOptionalContentRootReadModel("workflows", ".afar-research/workflows"),
]);
export type ProjectAfarOptionalContentRootReadModels =
  typeof ProjectAfarOptionalContentRootReadModels.Type;

export const DesignSystemCatalogEntry = Schema.Struct({
  slug: TrimmedNonEmptyString,
  displayName: TrimmedNonEmptyString,
  expectedRevision: Schema.NullOr(TrimmedNonEmptyString),
  designMdPath: TrimmedNonEmptyString,
  state: Schema.Literals(["draft", "ready", "invalid", "upgrade-required", "unsupported"]),
  selection: Schema.Union([
    Schema.Struct({ eligible: Schema.Literal(true) }),
    Schema.Struct({
      eligible: Schema.Literal(false),
      reason: TrimmedNonEmptyString,
    }),
  ]),
  findings: Schema.Array(TrimmedNonEmptyString),
});
export type DesignSystemCatalogEntry = typeof DesignSystemCatalogEntry.Type;

export const ProjectInspectAfarResult = Schema.Struct({
  project: Schema.Struct({
    rootPath: TrimmedNonEmptyString,
    designSystems: Schema.Array(DesignSystemCatalogEntry),
    optionalContentRoots: ProjectAfarOptionalContentRootReadModels,
    workspaces: ProjectAfarWorkspaceReadModels,
  }),
});
export type ProjectInspectAfarResult = typeof ProjectInspectAfarResult.Type;

export const ProjectAssessAfarInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
});
export type ProjectAssessAfarInput = typeof ProjectAssessAfarInput.Type;

export const ProjectAfarFinding = Schema.Struct({
  code: Schema.Literals(["unsupported-version", "unsafe-target", "access-failed"]),
  path: TrimmedNonEmptyString,
  operation: TrimmedNonEmptyString,
  message: TrimmedNonEmptyString,
  recovery: TrimmedNonEmptyString,
});
export type ProjectAfarFinding = typeof ProjectAfarFinding.Type;

export const ProjectActionReadinessEvent = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("repaired"),
    cwd: TrimmedNonEmptyString,
    message: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    type: Schema.Literal("blocked"),
    cwd: TrimmedNonEmptyString,
    finding: ProjectAfarFinding,
  }),
]);
export type ProjectActionReadinessEvent = typeof ProjectActionReadinessEvent.Type;

/**
 * Every real directory is an AFAR Project, so assessment only distinguishes a readable project
 * from one the App cannot access safely. Missing App-owned layout is created on first use.
 */
export const ProjectAssessAfarResult = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("ready"),
    inspection: ProjectInspectAfarResult,
  }),
  Schema.Struct({
    status: Schema.Literal("access-error"),
    cwd: TrimmedNonEmptyString,
    finding: ProjectAfarFinding,
    action: Schema.Literal("restore-access"),
  }),
]);
export type ProjectAssessAfarResult = typeof ProjectAssessAfarResult.Type;

export const ProjectInspectAfarFailure = Schema.Literals(["inspection-failed"]);
export type ProjectInspectAfarFailure = typeof ProjectInspectAfarFailure.Type;

export class ProjectInspectAfarError extends Schema.TaggedErrorClass<ProjectInspectAfarError>()(
  "ProjectInspectAfarError",
  {
    cwd: TrimmedNonEmptyString,
    reason: ProjectInspectAfarFailure,
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: {
    readonly cwd: string;
    readonly reason: ProjectInspectAfarFailure;
    readonly cause?: unknown;
    readonly message?: string;
  }) {
    super({
      ...props,
      message: props.message ?? `Failed to inspect AFAR Project '${props.cwd}'.`,
    } as any);
  }
}

export const ProjectEntriesFailure = Schema.Literals([
  "workspace_root_not_found",
  "workspace_root_create_failed",
  "workspace_root_stat_failed",
  "workspace_root_not_directory",
  "search_index_create_failed",
  "search_index_scan_timed_out",
  "search_index_search_failed",
  "search_index_refresh_failed",
]);
export type ProjectEntriesFailure = typeof ProjectEntriesFailure.Type;

type ProjectEntriesFailureContext = {
  readonly failure: ProjectEntriesFailure;
  readonly normalizedCwd?: string;
  readonly timeout?: string;
  readonly detail?: string;
  readonly cause?: unknown;
};

export class ProjectSearchEntriesError extends Schema.TaggedErrorClass<ProjectSearchEntriesError>()(
  "ProjectSearchEntriesError",
  {
    cwd: TrimmedNonEmptyString,
    queryLength: NonNegativeInt,
    limit: PositiveInt,
    failure: ProjectEntriesFailure,
    normalizedCwd: Schema.optional(TrimmedNonEmptyString),
    timeout: Schema.optional(TrimmedNonEmptyString),
    detail: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(
    props: ProjectEntriesFailureContext & {
      readonly cwd: string;
      readonly queryLength: number;
      readonly limit: number;
    },
  ) {
    super({
      ...props,
      message: `Failed to search workspace entries in '${props.cwd}'.`,
    } as any);
  }
}

export class ProjectListEntriesError extends Schema.TaggedErrorClass<ProjectListEntriesError>()(
  "ProjectListEntriesError",
  {
    cwd: TrimmedNonEmptyString,
    failure: ProjectEntriesFailure,
    normalizedCwd: Schema.optional(TrimmedNonEmptyString),
    timeout: Schema.optional(TrimmedNonEmptyString),
    detail: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: ProjectEntriesFailureContext & { readonly cwd: string }) {
    super({
      ...props,
      message: `Failed to list workspace entries in '${props.cwd}'.`,
    } as any);
  }
}

export class ProjectRefreshEntriesError extends Schema.TaggedErrorClass<ProjectRefreshEntriesError>()(
  "ProjectRefreshEntriesError",
  {
    cwd: TrimmedNonEmptyString,
    failure: ProjectEntriesFailure,
    normalizedCwd: Schema.optional(TrimmedNonEmptyString),
    timeout: Schema.optional(TrimmedNonEmptyString),
    detail: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: ProjectEntriesFailureContext & { readonly cwd: string }) {
    super({
      ...props,
      message: `Failed to refresh workspace files in '${props.cwd}'.`,
    } as any);
  }
}

export const ProjectReadFileInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString.check(Schema.isMaxLength(PROJECT_READ_FILE_PATH_MAX_LENGTH)),
});
export type ProjectReadFileInput = typeof ProjectReadFileInput.Type;

/**
 * The SHA-256 of a file's full contents, hex-encoded. An editor saves against
 * the digest it last read, so a write never lands over a change it has not seen.
 */
export const ProjectFileContentDigest = TrimmedNonEmptyString;
export type ProjectFileContentDigest = typeof ProjectFileContentDigest.Type;

export const ProjectReadFileResult = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
  contents: Schema.String,
  byteLength: NonNegativeInt,
  truncated: Schema.Boolean,
  contentDigest: Schema.optional(ProjectFileContentDigest),
});
export type ProjectReadFileResult = typeof ProjectReadFileResult.Type;

export const ProjectFileFailure = Schema.Literals([
  "workspace_path_outside_root",
  "resolved_path_outside_root",
  "path_not_file",
  "binary_file",
  "operation_failed",
  /** The file changed after the editor read it; the write was not applied. */
  "content_changed",
]);
export type ProjectFileFailure = typeof ProjectFileFailure.Type;

export const ProjectFileOperation = Schema.Literals([
  "realpath-workspace-root",
  "realpath-target",
  "open",
  "stat",
  "read",
  "close",
  "make-directory",
  "write-file",
]);
export type ProjectFileOperation = typeof ProjectFileOperation.Type;

type ProjectFileFailureContext = {
  readonly cwd: string;
  readonly relativePath: string;
  readonly failure: ProjectFileFailure;
  readonly resolvedPath?: string;
  readonly resolvedWorkspaceRoot?: string;
  readonly operation?: ProjectFileOperation;
  readonly operationPath?: string;
  readonly cause?: unknown;
};

export class ProjectReadFileError extends Schema.TaggedErrorClass<ProjectReadFileError>()(
  "ProjectReadFileError",
  {
    cwd: TrimmedNonEmptyString,
    relativePath: TrimmedNonEmptyString,
    failure: ProjectFileFailure,
    resolvedPath: Schema.optional(TrimmedNonEmptyString),
    resolvedWorkspaceRoot: Schema.optional(TrimmedNonEmptyString),
    operation: Schema.optional(ProjectFileOperation),
    operationPath: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: ProjectFileFailureContext) {
    super({
      ...props,
      message: `Failed to read workspace file '${props.relativePath}' in '${props.cwd}'.`,
    } as any);
  }
}

export const ProjectWriteFileInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString.check(Schema.isMaxLength(PROJECT_WRITE_FILE_PATH_MAX_LENGTH)),
  contents: Schema.String,
  /**
   * The digest the writer last read. When present, the write is refused with
   * `content_changed` unless the file still has exactly this content.
   */
  expectedContentDigest: Schema.optional(ProjectFileContentDigest),
});
export type ProjectWriteFileInput = typeof ProjectWriteFileInput.Type;

export const ProjectWriteFileResult = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
  contentDigest: Schema.optional(ProjectFileContentDigest),
});

/**
 * One file's current content identity, pushed when it changes on disk so an
 * open editor learns of an agent's write without polling. `null` when the file
 * no longer exists.
 */
export const ProjectFileChange = Schema.Struct({
  relativePath: TrimmedNonEmptyString,
  contentDigest: Schema.NullOr(ProjectFileContentDigest),
});
export type ProjectFileChange = typeof ProjectFileChange.Type;
export type ProjectWriteFileResult = typeof ProjectWriteFileResult.Type;

export class ProjectWriteFileError extends Schema.TaggedErrorClass<ProjectWriteFileError>()(
  "ProjectWriteFileError",
  {
    cwd: TrimmedNonEmptyString,
    relativePath: TrimmedNonEmptyString,
    failure: ProjectFileFailure,
    resolvedPath: Schema.optional(TrimmedNonEmptyString),
    resolvedWorkspaceRoot: Schema.optional(TrimmedNonEmptyString),
    operation: Schema.optional(ProjectFileOperation),
    operationPath: Schema.optional(TrimmedNonEmptyString),
    message: TrimmedNonEmptyString,
    cause: Schema.optional(Schema.Defect()),
  },
) {
  // @effect-diagnostics-next-line overriddenSchemaConstructor:off
  constructor(props: ProjectFileFailureContext) {
    super({
      ...props,
      message: `Failed to write workspace file '${props.relativePath}' in '${props.cwd}'.`,
    } as any);
  }
}
